/**
 * Centralized browser storage access.
 *
 * Nothing outside this module should touch `localStorage`/`sessionStorage`
 * directly. Routing every read and write through one place means the eventual
 * migration to the backend API is a change here rather than across the app,
 * and it removes the crash-on-corrupt-JSON footgun that raw
 * `JSON.parse(localStorage.getItem(...))` carries.
 */
import { clientLogger } from '@/services/telemetry'

export type StorageArea = 'local' | 'session'

export interface StorageDriver {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
  key(index: number): string | null
  readonly length: number
}

/**
 * Stands in when the real storage is unavailable — Safari private mode, disabled
 * cookies, SSR. Keeps the app functional for the session instead of throwing.
 */
function createMemoryDriver(): StorageDriver {
  const map = new Map<string, string>()

  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value)
    },
    removeItem: (key) => {
      map.delete(key)
    },
    key: (index) => [...map.keys()][index] ?? null,
    get length() {
      return map.size
    },
  }
}

function resolveDriver(area: StorageArea): StorageDriver {
  try {
    const candidate = area === 'local' ? window.localStorage : window.sessionStorage
    // Access alone can throw when storage is blocked by policy.
    const probe = '__masterhub_probe__'
    candidate.setItem(probe, probe)
    candidate.removeItem(probe)
    return candidate
  } catch {
    clientLogger.warn('Browser storage unavailable; using in-memory fallback', { area })
    return createMemoryDriver()
  }
}

export class StorageService {
  private readonly drivers = new Map<StorageArea, StorageDriver>()

  private driver(area: StorageArea): StorageDriver {
    const existing = this.drivers.get(area)
    if (existing) return existing

    const created = resolveDriver(area)
    this.drivers.set(area, created)
    return created
  }

  /** Raw string read. Prefer `getJSON` for structured values. */
  getRaw(key: string, area: StorageArea = 'local'): string | null {
    try {
      return this.driver(area).getItem(key)
    } catch (error) {
      clientLogger.warn('Storage read failed', { key, area, reason: String(error) })
      return null
    }
  }

  setRaw(key: string, value: string, area: StorageArea = 'local'): boolean {
    try {
      this.driver(area).setItem(key, value)
      return true
    } catch (error) {
      // Most often QuotaExceededError. Callers decide whether that is fatal.
      clientLogger.warn('Storage write failed', { key, area, reason: String(error) })
      return false
    }
  }

  /**
   * Reads and parses JSON, returning `fallback` when the key is absent or the
   * stored payload is corrupt. Corrupt entries are dropped so the app can
   * recover on the next write instead of failing forever.
   */
  getJSON<T>(key: string, fallback: T, area: StorageArea = 'local'): T {
    const raw = this.getRaw(key, area)
    if (raw === null) return fallback

    try {
      return JSON.parse(raw) as T
    } catch {
      clientLogger.warn('Discarding corrupt storage entry', { key, area })
      this.remove(key, area)
      return fallback
    }
  }

  setJSON(key: string, value: unknown, area: StorageArea = 'local'): boolean {
    try {
      return this.setRaw(key, JSON.stringify(value), area)
    } catch (error) {
      clientLogger.warn('Storage serialization failed', { key, area, reason: String(error) })
      return false
    }
  }

  remove(key: string, area: StorageArea = 'local'): void {
    try {
      this.driver(area).removeItem(key)
    } catch (error) {
      clientLogger.warn('Storage remove failed', { key, area, reason: String(error) })
    }
  }

  has(key: string, area: StorageArea = 'local'): boolean {
    return this.getRaw(key, area) !== null
  }

  /** Lists keys under a prefix. Used by maintenance and export routines. */
  keys(prefix?: string, area: StorageArea = 'local'): string[] {
    const driver = this.driver(area)
    const found: string[] = []

    for (let index = 0; index < driver.length; index += 1) {
      const key = driver.key(index)
      if (key && (!prefix || key.startsWith(prefix))) found.push(key)
    }

    return found
  }

  /** Removes every key under a prefix. Never clears storage wholesale. */
  removeByPrefix(prefix: string, area: StorageArea = 'local'): number {
    const matched = this.keys(prefix, area)
    matched.forEach((key) => this.remove(key, area))
    return matched.length
  }
}

export const storageService = new StorageService()
